import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { CancellationCreditPenaltySubCard } from "../Adjustments/CancellationCreditPenaltySubCard";
import { type AdjustmentsSectionItem } from "../Adjustments/Section";
import { getCancellationItems, getTimelinessItems } from "../Adjustments/utils";

function getCreditItems(items: AdjustmentsSectionItem[]) {
  return items.filter(
    (
      item
    ): item is AdjustmentsSectionItem & {
      pointsWithCredit: number;
      pointsWithoutCredit: number;
    } =>
      item.hasCreditOption === true &&
      isDefined(item.pointsWithCredit) &&
      isDefined(item.pointsWithoutCredit)
  );
}

function pickExamplePolicy(policy: ClipboardScorePolicy): ClipboardScorePolicy {
  // `points` and `pointsWithEmergencyCredit` are negative; a less-negative
  // `pointsWithEmergencyCredit` means a real credit savings.
  const [cancelExample] = (policy?.cancelShift ?? [])
    .filter(
      (tier) =>
        isDefined(tier.pointsWithEmergencyCredit) && tier.pointsWithEmergencyCredit > tier.points
    )
    .sort((a, b) => (a.leadTime[0] ?? 0) - (b.leadTime[0] ?? 0));

  const [latenessExample] = (policy?.latenessConfig ?? [])
    .filter(
      (tier) =>
        isDefined(tier.pointsWithEmergencyCredit) && tier.pointsWithEmergencyCredit > tier.points
    )
    .sort((a, b) => a.minutesLateRange.startFrom - b.minutesLateRange.startFrom);

  return {
    ...policy,
    cancelShift: isDefined(cancelExample) ? [cancelExample] : [],
    latenessConfig: isDefined(latenessExample) ? [latenessExample] : [],
  };
}

interface ScheduleChangesStepProps {
  readonly policy: ClipboardScorePolicy;
}

export function ScheduleChangesStep(props: ScheduleChangesStepProps) {
  const { policy } = props;
  const examplePolicy = pickExamplePolicy(policy);

  const cancellationCreditItems = getCreditItems(
    getCancellationItems({ policy: examplePolicy, showCreditInfo: true, hasCredit: true })
  );

  const latenessCreditItems = getCreditItems(
    getTimelinessItems({ policy: examplePolicy, showCreditInfo: true, hasCredit: true })
  );

  return (
    <>
      <Text variant="h3" sx={{ fontWeight: 400 }}>
        When your schedule changes, you&apos;re covered
      </Text>
      <Stack spacing={5} sx={{ width: "100%", textAlign: "left" }}>
        {[...cancellationCreditItems, ...latenessCreditItems].map((item) => (
          <CancellationCreditPenaltySubCard
            key={item.title}
            hasCredit
            title={item.title}
            pointsWithCredit={item.pointsWithCredit}
            pointsWithoutCredit={item.pointsWithoutCredit}
          />
        ))}
      </Stack>
    </>
  );
}
